import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { rupiah, numberID, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Megaphone } from "lucide-react";

export default function Advertising() {
  const { can } = useAuth();
  const { masters } = useMasters();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => { setRows(null); api.get("/advertising").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(load, []);
  return (
    <div>
      <PageHeader title="Iklan" subtitle="Iklan tercatat per toko. ROAS, CPC, dan Conversion Rate dihitung otomatis." testId="ads-header">
        {can("advertising.create") && <Button onClick={() => setOpen(true)} data-testid="create-ad-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Catat Iklan</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada data iklan" icon={Megaphone} /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Toko</TableHead><TableHead>Marketplace</TableHead><TableHead>Campaign</TableHead><TableHead className="text-right">Belanja</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">ROAS</TableHead><TableHead className="text-right">CPC</TableHead><TableHead className="text-right">Konversi</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.id} data-testid={`ad-row-${a.id}`}>
                    <TableCell>{formatDate(a.tanggal)}</TableCell><TableCell>{a.store_name}</TableCell><TableCell>{a.marketplace_name}</TableCell>
                    <TableCell className="font-medium">{a.campaign_name}</TableCell>
                    <TableCell className="text-right">{rupiah(a.ad_spend)}</TableCell><TableCell className="text-right">{rupiah(a.revenue)}</TableCell>
                    <TableCell className="text-right font-semibold text-indigo-700">{a.roas}x</TableCell><TableCell className="text-right">{rupiah(a.cpc)}</TableCell>
                    <TableCell className="text-right">{a.conversion_rate}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {open && <AdDialog masters={masters} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function AdDialog({ masters, onClose, onSaved }) {
  const today = wibToday();
  const [form, setForm] = useState({ tanggal: today, store_id: "", marketplace_id: "", campaign_name: "", ad_spend: 0, revenue: 0, orders: 0, clicks: 0, impressions: 0, notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.store_id || !form.marketplace_id || !form.campaign_name) return toast.error("Lengkapi toko, marketplace, dan campaign");
    setSaving(true);
    try {
      await api.post("/advertising", { ...form, ad_spend: Number(form.ad_spend), revenue: Number(form.revenue), orders: Number(form.orders), clicks: Number(form.clicks), impressions: Number(form.impressions) });
      toast.success("Iklan dicatat"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Catat Data Iklan</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <F label="Tanggal"><Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} data-testid="ad-date" /></F>
          <F label="Campaign"><Input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} data-testid="ad-campaign" /></F>
          <F label="Toko"><S value={form.store_id} onChange={(v) => setForm({ ...form, store_id: v })} options={masters.stores} testId="ad-store" /></F>
          <F label="Marketplace"><S value={form.marketplace_id} onChange={(v) => setForm({ ...form, marketplace_id: v })} options={masters.marketplaces} testId="ad-marketplace" /></F>
          <F label="Belanja Iklan (Rp)"><Input type="number" value={form.ad_spend} onChange={(e) => setForm({ ...form, ad_spend: e.target.value })} data-testid="ad-spend" /></F>
          <F label="Revenue dari Iklan (Rp)"><Input type="number" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} data-testid="ad-revenue" /></F>
          <F label="Orders"><Input type="number" value={form.orders} onChange={(e) => setForm({ ...form, orders: e.target.value })} /></F>
          <F label="Clicks"><Input type="number" value={form.clicks} onChange={(e) => setForm({ ...form, clicks: e.target.value })} /></F>
          <F label="Impressions"><Input type="number" value={form.impressions} onChange={(e) => setForm({ ...form, impressions: e.target.value })} /></F>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-ad-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
const F = ({ label, children }) => <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
const S = ({ value, onChange, options, testId }) => <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testId}><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>;
